import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useState } from "react";

interface Props {
  languages: string[];
  topics: string[];
  onSearch: (q: string) => void;
  onLanguage: (lang: string) => void;
  onTopic: (topic: string) => void;
  search: string;
  language: string;
  topic: string;
}

export function FilterBar({
  languages,
  topics,
  onSearch,
  onLanguage,
  onTopic,
  search,
  language,
  topic,
}: Props) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Search box */}
      <div
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
          focused
            ? "border-[var(--color-border-active)] shadow-[0_0_0_3px_var(--color-primary-muted)]"
            : "border-border"
        } bg-bg-card`}
      >
        <MagnifyingGlass
          size={18}
          className="text-text-muted shrink-0"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Search repositories…"
          aria-label="Search repositories"
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
        />
        {search && (
          <button
            onClick={() => onSearch("")}
            aria-label="Clear search"
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filter chips row */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Language filter */}
        <select
          value={language}
          onChange={(e) => onLanguage(e.target.value)}
          aria-label="Filter by language"
          className="text-sm px-3 py-1.5 rounded-lg border border-border bg-bg-card text-text-secondary cursor-pointer outline-none focus-visible:border-primary"
        >
          <option value="">All languages</option>
          {languages.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>

        {/* Topic pills */}
        {topics.slice(0, 8).map((t) => (
          <button
            key={t}
            onClick={() => onTopic(topic === t ? "" : t)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              topic === t
                ? "border-primary bg-primary-muted text-primary"
                : "border-border text-text-muted hover:border-primary hover:text-primary-light"
            }`}
          >
            {t}
          </button>
        ))}

        {/* Clear filters */}
        {(language || topic) && (
          <button
            onClick={() => {
              onLanguage("");
              onTopic("");
            }}
            className="text-xs px-3 py-1.5 rounded-full text-error border border-[var(--color-error)]/30 hover:bg-error/10 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
